import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "../Trading/CurrencyPairManagementPage.css";

const StakingPackagesPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [currencySearch, setCurrencySearch] = useState("");
  const [debouncedCurrency, setDebouncedCurrency] = useState("");
  const [listModal, setListModal] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCurrency(currencySearch.trim()), 1000);
    return () => clearTimeout(t);
  }, [currencySearch]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedCurrency, rowsPerPage]);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        limit: rowsPerPage,
      };
      if (statusFilter === "ACTIVE" || statusFilter === "INACTIVE") {
        params.status = statusFilter;
      } else {
        params.status = "all";
      }
      if (debouncedCurrency) params.currency = debouncedCurrency;

      const res = await AdminService.getEarningPackages(params);
      const list = Array.isArray(res?.data) ? res.data : [];
      setPackages(list);
      setTotalPages(res?.pagination?.total_pages || 1);
      setTotalCount(
        typeof res?.pagination?.total === "number" ? res.pagination.total : list.length
      );
      if (res?.success === false && !list.length) {
        setError(res?.message || "Failed to load packages.");
      } else {
        setError("");
      }
    } catch (e) {
      console.error(e);
      setPackages([]);
      setError("Failed to load packages.");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, debouncedCurrency]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const pkgId = (p) => p._id || p.id;

  const formatStatus = (s) => {
    const u = String(s || "").toUpperCase();
    if (u === "ACTIVE") return "Active";
    if (u === "INACTIVE") return "Inactive";
    return s || "—";
  };

  const isActive = (p) => String(p.status || "").toUpperCase() === "ACTIVE";

  const formatDecimal = (v) => {
    if (v == null) return "—";
    if (typeof v === "object" && v.$numberDecimal != null) return String(v.$numberDecimal);
    return String(v);
  };

  const formatCancellationFee = (row) =>
    formatDecimal(row.cancellation_fee ?? row.cancellationFee);

  const handleToggleStatus = async (id) => {
    const pkg = packages.find((p) => pkgId(p) === id);
    if (!pkg || toggleLoading) return;
    const next = isActive(pkg) ? "INACTIVE" : "ACTIVE";
    setToggleLoading(true);
    try {
      const res = await AdminService.updateEarningPackage(id, { status: next });
      if (res?.success) {
        setListModal(null);
        fetchPackages();
      } else {
        setError(res?.message || "Could not update status.");
      }
    } catch (e) {
      console.error(e);
      setError("Could not update status.");
    } finally {
      setToggleLoading(false);
    }
  };

  const pageStart = packages.length === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const pageEnd = packages.length === 0 ? 0 : (page - 1) * rowsPerPage + packages.length;

  return (
    <main className="dashboard-main dashboard-subpage currency-pair-page">
      <section className="dashboard-hero currency-pair-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Staking</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Staking Packages</span>
          </p>
          <h1 className="dashboard-hero__title">Staking Packages</h1>
          <p className="dashboard-hero__subtitle">View and manage earning packages</p>
        </div>
        <div className="currency-pair-hero__actions">
          <Link to="/dashboard/staking/add-package" className="cp-btn cp-btn--primary cp-btn--add">
            <i className="fas fa-plus" /> Add Package
          </Link>
        </div>
        <div className="dashboard-hero__icon currency-pair-hero__icon">
          <i className="fas fa-coins" />
        </div>
      </section>

      <div className="dashboard-body currency-pair-body">
        <div className="currency-pair-card currency-pair-card--table-only">
          <div className="currency-pair-table-block">
            <div className="currency-pair-table-head">
              <div className="currency-pair-section-head">
                <i className="fas fa-list" />
                <h2>
                  Packages
                  {totalCount > 0 && (
                    <span style={{ fontWeight: 400, opacity: 0.85, marginLeft: 8 }}>
                      ({totalCount} total)
                    </span>
                  )}
                </h2>
              </div>
              <div className="cp-table-actions">
                <div className="cp-filter-wrap">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="cp-filter-select"
                  >
                    <option value="">All status</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </div>
              </div>
            </div>

            {error && (
              <p style={{ color: "#dc2626", padding: "0 1rem", margin: "0 0 0.5rem" }}>{error}</p>
            )}

            <div className="cp-search-wrap">
              <i className="fas fa-search" />
              <input
                type="text"
                placeholder="Currency short name (partial match, 1s debounce)…"
                value={currencySearch}
                onChange={(e) => setCurrencySearch(e.target.value)}
                className="cp-search-input"
              />
            </div>
            <div className="cp-table-wrap">
              {loading ? (
                <p style={{ padding: "2rem", textAlign: "center" }}>
                  <i className="fas fa-spinner fa-spin" /> Loading…
                </p>
              ) : (
                <table className="cp-table">
                  <thead>
                    <tr>
                      <th>Currency</th>
                      <th>Return %</th>
                      <th>Duration (days)</th>
                      <th>Min amount</th>
                      <th>Cancel fee</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packages.map((row) => {
                      const id = pkgId(row);
                      const cur = row.currency ?? row.currency_short_name ?? row.short_name ?? "—";
                      return (
                        <tr key={id}>
                          <td>
                            <strong>{cur}</strong>
                            {row.currency_fullname && row.currency_fullname !== cur && (
                              <span style={{ opacity: 0.75, display: "block", fontSize: "0.85em" }}>
                                {row.currency_fullname}
                              </span>
                            )}
                          </td>
                          <td>{row.return_percentage != null ? `${row.return_percentage}%` : "—"}</td>
                          <td>{row.duration_days ?? "—"}</td>
                          <td>{formatDecimal(row.min_amount)}</td>
                          <td>{formatCancellationFee(row)}</td>
                          <td>
                            <span
                              className={`cp-badge cp-badge--${isActive(row) ? "global" : "local"}`}
                            >
                              {formatStatus(row.status)}
                            </span>
                          </td>
                          <td>
                            <div className="cp-action-btns">
                              <Link
                                to={`/dashboard/staking/edit-package/${id}`}
                                className="cp-action-btn cp-action-btn--list"
                              >
                                <i className="fas fa-edit" /> Edit
                              </Link>
                              <button
                                type="button"
                                className={`cp-action-btn ${isActive(row) ? "cp-action-btn--delist" : "cp-action-btn--list"}`}
                                onClick={() => setListModal(id)}
                                disabled={toggleLoading}
                                title={isActive(row) ? "Deactivate" : "Activate"}
                              >
                                <i className={`fas ${isActive(row) ? "fa-pause" : "fa-play"}`} />
                                {isActive(row) ? "Deactivate" : "Activate"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {!loading && packages.length === 0 && (
              <p className="openorders-empty" style={{ padding: "2rem", textAlign: "center" }}>
                No packages match your filters.
              </p>
            )}
            <div className="cp-pagination">
              <div className="cp-pagination-rows">
                <span>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
              </div>
              <div className="cp-pagination-info">
                {pageStart}-{pageEnd} of {totalCount || packages.length}
              </div>
              <div className="cp-pagination-nav">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  className="cp-pagination-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <i className="fas fa-chevron-left" />
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  className="cp-pagination-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <i className="fas fa-chevron-right" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {listModal &&
        (() => {
          const pkg = packages.find((p) => pkgId(p) === listModal);
          if (!pkg) return null;
          const active = isActive(pkg);
          const cur = pkg.currency ?? pkg.currency_short_name ?? "package";
          return (
            <div className="cp-modal-overlay" onClick={() => !toggleLoading && setListModal(null)}>
              <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
                <h3>{active ? "Deactivate package" : "Activate package"}</h3>
                <p>
                  {active
                    ? `Deactivate ${cur}? Users won’t be able to subscribe to this package.`
                    : `Activate ${cur}? Users will be able to subscribe.`}
                </p>
                <div className="cp-modal-actions">
                  <button
                    type="button"
                    className="cp-btn cp-btn--outline"
                    onClick={() => setListModal(null)}
                    disabled={toggleLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="cp-btn cp-btn--primary"
                    onClick={() => handleToggleStatus(listModal)}
                    disabled={toggleLoading}
                  >
                    {toggleLoading ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      <i className="fas fa-check" />
                    )}{" "}
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </main>
  );
};

export default StakingPackagesPage;
